import React from 'react';

import {
  InfoFactLabel,
  InfoFactRow,
  InfoFacts,
  InfoFactValue,
  InfoSection,
  InfoSectionTitle,
  InfoText,
} from '@internal/components/application-view/project-info-drawer.styled';
import { Project } from '@internal/types/projects';

const NOT_PROVIDED = 'Not provided';

interface Fact {
  label: string;
  value?: string | null;
}

const weeklyInvolvement = (value?: string): string | null =>
  value ? value.replace(/h$/u, ' hours / week') : null;

const hourlyRange = (project: Project): string | null =>
  project.desiredRateMin && project.desiredRateMax
    ? `$${project.desiredRateMin}–$${project.desiredRateMax} / hour`
    : null;

const proposedRate = (project: Project): string | null => {
  if (!(Number(project.candidateRatePerHour) > 0)) return null;
  const comment = project.rateCommentForDev
    ? ` · ${project.rateCommentForDev}`
    : '';
  return `$${project.candidateRatePerHour}/hour${comment}`;
};

const workingHours = (project: Project): string | null =>
  project.workingHoursFrom && project.workingHoursTo
    ? `${project.workingHoursFrom}–${project.workingHoursTo}`
    : null;

const rateFacts = (project: Project): Fact[] => {
  const range = hourlyRange(project);
  const comment = project.candidateRateComment?.trim();
  const proposed = proposedRate(project);
  if (proposed && !range && !comment) {
    return [{ label: 'Proposed hourly rate', value: proposed }];
  }
  return [
    { label: 'Hourly rate range (USD)', value: range },
    { label: 'Rate comment', value: comment },
  ];
};

const availabilityFacts = (project: Project): Fact[] => {
  return [
    { label: 'Preferred start', value: project.readyToStartAt },
    ...rateFacts(project),
    {
      label: 'Involvement',
      value: weeklyInvolvement(project.projectWeeklyInvolvement),
    },
    { label: 'Planned vacations', value: project.vacationPlans },
    {
      label: 'Client interview availability',
      value: project.interviewTimeslots,
    },
    { label: 'Working hours', value: workingHours(project) },
    { label: 'Time zone', value: project.timeZone },
  ];
};

const FactList: React.FC<{ facts: Fact[] }> = ({ facts }) => (
  <InfoFacts>
    {facts.map(fact => (
      <InfoFactRow key={fact.label}>
        <InfoFactLabel>{fact.label}</InfoFactLabel>
        <InfoFactValue>{fact.value?.trim() || NOT_PROVIDED}</InfoFactValue>
      </InfoFactRow>
    ))}
  </InfoFacts>
);

const ApplicationSummary: React.FC<{ project: Project }> = ({ project }) => {
  const questions: Fact[] = (project.projectAnswers ?? []).map(answer => ({
    label: answer.originalQuestionTitle ?? '',
    value: answer.text,
  }));

  return (
    <>
      <InfoSection data-testid="application-summary-availability">
        <InfoSectionTitle>Availability</InfoSectionTitle>
        <FactList facts={availabilityFacts(project)} />
      </InfoSection>

      {questions.length > 0 && (
        <InfoSection data-testid="application-summary-questions">
          <InfoSectionTitle>Project questions</InfoSectionTitle>
          <FactList facts={questions} />
        </InfoSection>
      )}

      <InfoSection data-testid="application-summary-comments">
        <InfoSectionTitle>Additional comments</InfoSectionTitle>
        <InfoText>
          {project.candidateApplicationComment?.trim() || NOT_PROVIDED}
        </InfoText>
      </InfoSection>
    </>
  );
};

export default ApplicationSummary;
